import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  linkedSignal,
  output,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatMenuModule } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { APP_DATE_FORMATS, DateFnsAdapter } from '@blaze-runtime/engine-feature';
import { DatasetColumn } from '@blaze-shared/schema-domain';
import { DatepickerHeaderComponent } from '@blaze-shared/shared-feature';
import { InputMaskDirective } from '@blaze-shared/shared-ui';
import { isNullish, isValidDate } from '@blaze-shared/shared-util';

import {
  FIELD_INPUT_PIPES,
  IsColumnTypePipe,
  STANDALONE_FIELD_INPUT_PIPES,
} from '../../../../field-input/field-input.pipe';

@UntilDestroy()
@Component({
  selector: 'blaze-cell-date',
  templateUrl: './cell-date.component.html',
  styleUrls: ['./cell-date.component.scss', '../../base-cell-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, InputMaskDirective, IsColumnTypePipe, MatMenuModule, MatDatepickerModule],
  providers: [
    ...FIELD_INPUT_PIPES,
    ...STANDALONE_FIELD_INPUT_PIPES,
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: APP_DATE_FORMATS,
    },
  ],
})
export class CellDateComponent implements AfterViewInit {
  column = input.required<DatasetColumn>();
  rowNum = input.required<number>();
  value = input<unknown>();
  isDisabled = input<boolean>(false);

  pickerClosed = output();
  pickerOpened = output();
  valueChanged = output<string | null>();

  dateInputRef = viewChild('dateInput', { read: ElementRef });
  datepicker = viewChild<MatDatepicker<Date>>('datepicker');

  private readonly dateValue = computed<Date | null | undefined>(() => {
    const value = this.value();
    if (value === null || value === undefined || !value) {
      return null;
    }

    if (typeof value === 'string') {
      return this.transformStringDateToDate(value);
    }
    throw new TypeError(
      `CellDateComponent: value is expected to be string | null | undefined, got ${typeof value}`,
    );
  });

  protected datepickerHeader = DatepickerHeaderComponent;

  protected internalValue = linkedSignal<Date | null>(() => {
    const dateValue = this.dateValue();
    return dateValue ? new Date(dateValue) : null;
  });

  ngAfterViewInit(): void {
    const datepicker = this.datepicker();
    if (!datepicker) {
      throw new Error('Expected datepicker to be defined');
    }
    datepicker.closedStream.pipe(untilDestroyed(this)).subscribe(() => {
      this.pickerClosed.emit();
    });
  }

  openDatePicker(): void {
    setTimeout(() => {
      this.dateInputRef()?.nativeElement?.click();
      this.datepicker()?.open();
    });
  }

  closeDatePicker(): void {
    this.datepicker()?.close();
  }

  protected afterDatePickerOpen(): void {
    // It's brutal but we need to focus input again and [restoreFocus]="true" doesn't work here (we already mess with focus)
    setTimeout(() => {
      this.dateInputRef()?.nativeElement?.focus();
      this.dateInputRef()?.nativeElement?.select();
      // hiding backdrop for calendar
      // WARNING: I assume that this is one element before calendar host element
      document
        .querySelector('.cdk-overlay-pane mat-calendar')
        ?.closest('.cdk-overlay-connected-position-bounding-box')
        ?.previousElementSibling?.classList.add('hidden');
      this.pickerOpened.emit();
    }, 500);
  }

  protected onDateChange(value: Date | null): void {
    if (isNullish(value)) {
      this.internalValue.set(null);
      this.valueChanged.emit(null);
      return;
    }

    let newValue: Date | null = null;
    if (value instanceof Date) {
      newValue = this.transformStringDateToDate(this.prepareDateToOutput(value));
    } else {
      throw new TypeError(
        `CellDateComponent: value is expected to be Date | null, got ${typeof value}`,
      );
    }

    if (!newValue || !isValidDate(newValue)) {
      this.internalValue.set(null);
      this.valueChanged.emit(null);
      return;
    }

    this.internalValue.set(newValue);
    this.valueChanged.emit(this.prepareDateToOutput(value));
  }

  protected moveCarriage(step: number | 'end', input: HTMLInputElement): void {
    if (step === 'end') {
      input.selectionStart = input.selectionEnd;
    } else if (input.selectionEnd === input.selectionStart) {
      const newIndex = (input.selectionEnd || 0) + step;
      input.selectionEnd = newIndex;
      input.selectionStart = newIndex;
    }
  }

  private prepareDateToOutput(date: string | Date): string {
    if (typeof date === 'string') {
      return date.split('T')[0];
    }
    const dateDate = date.getDate().toString().padStart(2, '0');
    const dateMonth = (date.getMonth() + 1).toString().padStart(2, '0');
    const dateYear = date.getFullYear().toString().padStart(4, '0');
    const valueToEmit = `${dateYear}-${dateMonth}-${dateDate}`;
    return valueToEmit;
  }

  private transformStringDateToDate(value: string): Date {
    const tzOffset = new Date(value + 'T00:00:00').getTimezoneOffset();
    const tzOffsetHours = Math.floor(tzOffset / 60)
      .toString()
      .padStart(2, '0');
    const tzOffsetMinutes = (tzOffset % 60).toString().padStart(2, '0');

    return new Date(
      value + (tzOffset > 0 ? `T${tzOffsetHours}:${tzOffsetMinutes}:00` : 'T00:00:00'),
    );
  }
}
